import { createFileRoute } from "@tanstack/react-router"

import { Icon } from "@/components/icon/Icon"
import { LegalToc } from "@/components/legal/LegalToc"
import { Reveal } from "@/components/reveal/Reveal"
import { Seo } from "@/components/seo/Seo"
import { LAST_UPDATED_DATE, REGISTERED_OFFICE, SUPPORT_EMAIL } from "@/constants/company"

export const Route = createFileRoute("/terms")({
	component: TermsAndConditions,
})

const SECTIONS = [
	{ id: "clause-1", label: "1. What We Do" },
	{ id: "clause-2", label: "2. What We Do Not Do" },
	{ id: "clause-3", label: "3. Your Account" },
	{ id: "clause-4", label: "4. What You Send Us" },
	{ id: "clause-5", label: "5. Professionals Acting For Clients" },
	{ id: "clause-6", label: "6. Availability" },
	{ id: "clause-7", label: "7. Ending Your Use" },
	{ id: "clause-8", label: "8. Changes To These Terms" },
	{ id: "clause-9", label: "9. Contact" },
]

const BODY = "text-body-md text-on-surface-variant font-sans leading-relaxed"

function Clause({ n, title, children }: { n: string; title: string; children: React.ReactNode }) {
	return (
		<section
			id={`clause-${n}`}
			className="gap-space-sm flex scroll-mt-28 flex-col"
		>
			<div className="gap-space-sm flex items-baseline">
				<span className="text-label-md text-primary font-sans font-bold">
					{n.padStart(2, "0")}.
				</span>
				<h2 className="text-headline-md text-on-surface font-serif font-semibold">
					{title}
				</h2>
			</div>
			{children}
		</section>
	)
}

function Points({ items }: { items: string[] }) {
	return (
		<ul className={`pl-space-md flex list-disc flex-col gap-1 ${BODY}`}>
			{items.map(item => (
				<li key={item}>{item}</li>
			))}
		</ul>
	)
}

function TermsAndConditions() {
	return (
		<div className="flex flex-col">
			<Seo
				title="Terms of Service"
				description="The terms covering your use of Swamithra's online service: what we do, what we do not do, your account and what you send us."
				path="/terms"
			/>
			<section className="bg-surface-container-low py-space-xl w-full">
				<div className="px-margin mx-auto max-w-7xl">
					<div className="mt-space-sm gap-space-md flex flex-col justify-between md:flex-row md:items-end">
						<div>
							<span className="text-label-md text-primary mb-1 block font-sans tracking-widest uppercase">
								Legal Agreement
							</span>
							<h1 className="text-display-lg text-on-surface font-serif font-semibold tracking-tight">
								Terms of Service
							</h1>
						</div>
						<div className="gap-space-sm bg-surface-container px-space-md py-space-xs flex items-center self-start rounded md:self-auto">
							<Icon
								name="history_edu"
								size={18}
								className="text-tertiary"
							/>
							<span className="text-label-sm text-on-surface-variant font-sans uppercase">
								Last updated:{" "}
								<strong className="text-on-surface font-semibold">
									{LAST_UPDATED_DATE}
								</strong>
							</span>
						</div>
					</div>
					<p className="mt-space-sm text-body-md text-on-surface-variant max-w-[68ch] font-sans leading-relaxed">
						These terms cover your use of Swamithra Tax KPO Private Limited&rsquo;s
						online service. Using the service means you accept them.
					</p>
				</div>
			</section>

			<section className="py-space-xl w-full">
				<div className="px-margin-mobile sm:px-margin mx-auto max-w-275">
					<div className="gap-space-xl grid grid-cols-1 items-start lg:grid-cols-12">
						<aside className="sticky top-24 lg:col-span-4">
							<LegalToc
								sections={SECTIONS}
								title="Table of Clauses"
							/>
						</aside>

						<article className="gap-space-xl flex max-w-[68ch] flex-col lg:col-span-8">
							<Reveal>
								<Clause
									n="1"
									title="What We Do"
								>
									<p className={BODY}>
										Swamithra connects Individual Taxpayers and Professionals
										with a panel of former Indian Revenue Service (IRS) officers
										to answer your Income Tax queries, and review your draft
										response before you file it.
									</p>
								</Clause>
							</Reveal>

							<Reveal>
								<section
									id="clause-2"
									className="scroll-mt-28"
								>
									<div className="bg-surface-container-high p-space-lg relative overflow-hidden rounded-xl shadow-sm">
										<div className="bg-primary absolute top-0 bottom-0 left-0 w-1.5" />
										<div className="mb-space-xs gap-space-xs text-primary flex items-center">
											<Icon
												name="gavel"
												size={24}
											/>
											<span className="text-label-md font-sans font-bold tracking-widest uppercase">
												Scope of the Service
											</span>
										</div>
										<h2 className="mb-space-sm text-headline-md text-on-surface font-serif font-semibold">
											02. What We Do Not Do
										</h2>
										<div className="bg-surface-container-lowest p-space-md rounded">
											<ul className="text-body-md text-on-surface pl-space-md flex list-disc flex-col gap-1 font-sans leading-relaxed">
												<li>
													We do not file returns or replies on your
													behalf.
												</li>
												<li>
													We do not represent you before the Income-tax
													Department.
												</li>
												<li>
													An answer is professional guidance on the
													material you send us. It is not a substitute for
													a formal opinion where one is required.
												</li>
											</ul>
										</div>
									</div>
								</section>
							</Reveal>

							<Reveal>
								<Clause
									n="3"
									title="Your Account"
								>
									<Points
										items={[
											"You sign in with a WhatsApp number and an OTP. There is no password.",
											"A session stays open for 24 hours of inactivity.",
											"An OTP reaches you on WhatsApp only, and it works for 10 minutes.",
											"Nobody from Swamithra will ever ask you to read an OTP out. Treat anyone who does as an impostor.",
										]}
									/>
								</Clause>
							</Reveal>

							<Reveal>
								<Clause
									n="4"
									title="What You Send Us"
								>
									<Points
										items={[
											"You confirm you are entitled to share the documents you upload.",
											"Answers rest on what you send. Incomplete or altered documents produce answers that do not hold.",
											"A question cannot be edited once sent. Add corrections as a follow-up in the same thread.",
											"A closed question cannot be reopened. Start a new one.",
										]}
									/>
								</Clause>
							</Reveal>

							<Reveal>
								<Clause
									n="5"
									title="Professionals Acting For Clients"
								>
									<p className={BODY}>
										If you are a professional acting on behalf of your client,
										queries you raise for your clients sit under your account,
										and you are responsible for having their permission to share
										their documents with us.
									</p>
								</Clause>
							</Reveal>

							<Reveal>
								<Clause
									n="6"
									title="Availability"
								>
									<p className={BODY}>
										We aim to keep the service running and to reply promptly,
										but we do not guarantee uninterrupted access or a fixed
										response time.
									</p>
								</Clause>
							</Reveal>

							<Reveal>
								<Clause
									n="7"
									title="Ending Your Use"
								>
									<p className={BODY}>
										You may stop using the service at any time and ask us to
										close your account. We may suspend an account that is used
										to abuse the service.
									</p>
								</Clause>
							</Reveal>

							<Reveal>
								<Clause
									n="8"
									title="Changes To These Terms"
								>
									<p className={BODY}>
										If these terms change materially we will say so in the app
										before the change takes effect. The date above is when this
										text last changed.
									</p>
								</Clause>
							</Reveal>

							<Reveal>
								<Clause
									n="9"
									title="Contact"
								>
									<div className="bg-surface-container-low p-space-lg rounded-xl shadow-sm">
										<p className="text-body-md text-on-surface font-sans font-semibold">
											Swamithra Tax KPO Private Limited, {REGISTERED_OFFICE}
										</p>
										<p className="text-body-md text-on-surface-variant mt-1 font-sans">
											Questions about these terms go to{" "}
											<a
												href={`mailto:${SUPPORT_EMAIL}`}
												className="text-primary font-semibold hover:underline"
											>
												{SUPPORT_EMAIL}
											</a>
											.
										</p>
									</div>
								</Clause>
							</Reveal>
						</article>
					</div>
				</div>
			</section>
		</div>
	)
}
